import { isProjectWidgetMode } from '../routes'

import { getBackendBaseUrl } from './backendUrl'

/** In-memory only — never localStorage. */
let widgetSession = null
/** Dedupes concurrent GET …/token calls for the same widget code. */
let inflightTokenRefresh = null
let inflightWidgetCode = null

export const isMongoObjectId = (value) =>
    /^[a-f\d]{24}$/i.test(String(value || '').trim())

export const setWidgetSession = ({
    token,
    projectId,
    projectName,
    widgetCode,
}) => {
    if (!token || !projectId) {
        widgetSession = null
        return
    }
    widgetSession = {
        token: String(token),
        projectId: String(projectId),
        projectName: projectName ? String(projectName) : '',
        widgetCode: widgetCode ? String(widgetCode) : '',
    }
}

export const clearWidgetSession = () => {
    widgetSession = null
    inflightTokenRefresh = null
    inflightWidgetCode = null
}

export const getWidgetToken = () => widgetSession?.token ?? null

export const getWidgetProjectId = () => widgetSession?.projectId ?? null

export const getWidgetCodeFromSession = () => widgetSession?.widgetCode ?? ''

export const isWidgetAuthActive = () => Boolean(widgetSession?.token)

export const isActiveProjectWidgetMode = () => {
    if (typeof window === 'undefined') return false
    return isProjectWidgetMode(window.location.search)
}

export const getWidgetCodeFromLocation = () => {
    if (typeof window === 'undefined') return null
    const match = window.location.pathname.match(/\/projects\/([^/]+)/)
    return match?.[1] ?? null
}

export const resolveWidgetCodeForRoute = (routeId, search = '') => {
    const params = new URLSearchParams(
        String(search || '').startsWith('?') ? search.slice(1) : search
    )
    const fromQuery = params.get('widgetCode')
    if (fromQuery) return fromQuery

    const id = String(routeId || '').trim()
    if (!id) return null
    if (!isMongoObjectId(id)) return id
    return getWidgetCodeFromSession() || null
}

/**
 * Public — GET support/widget/:widgetCode/token
 * Reuses the in-memory session for the same code unless `force` is set
 * (e.g. axios retry after "token invalid").
 */
export const refreshWidgetTokenFromApi = async (
    widgetCode,
    { force = false } = {}
) => {
    const code = String(widgetCode || '').trim()
    if (!code) {
        throw new Error('Invalid widget code')
    }

    if (force) {
        widgetSession = null
    }

    if (
        !force &&
        widgetSession?.token &&
        widgetSession?.projectId &&
        widgetSession?.widgetCode === code
    ) {
        return {
            success: true,
            token: widgetSession.token,
            projectId: widgetSession.projectId,
            projectName: widgetSession.projectName,
        }
    }

    if (inflightTokenRefresh && inflightWidgetCode === code) {
        return inflightTokenRefresh
    }

    const request = (async () => {
        const response = await fetch(
            `${getBackendBaseUrl()}/support/widget/${encodeURIComponent(code)}/token`,
            {
                method: 'GET',
                headers: { Accept: 'application/json' },
            }
        )

        let body = {}
        try {
            body = await response.json()
        } catch {
            body = {}
        }

        if (
            !response.ok ||
            !body?.success ||
            !body?.token ||
            !body?.projectId
        ) {
            clearWidgetSession()
            throw new Error(body?.message || 'Failed to load widget token')
        }

        setWidgetSession({
            token: body.token,
            projectId: body.projectId,
            projectName: body.projectName,
            widgetCode: code,
        })

        return body
    })()

    inflightWidgetCode = code
    inflightTokenRefresh = request

    try {
        return await request
    } finally {
        if (inflightTokenRefresh === request) {
            inflightTokenRefresh = null
            inflightWidgetCode = null
        }
    }
}

export const isWidgetTokenInvalidResponse = (errorOrResponse) => {
    const status = errorOrResponse?.response?.status ?? errorOrResponse?.status
    const message =
        errorOrResponse?.response?.data?.message ??
        errorOrResponse?.data?.message ??
        ''
    return status === 403 && String(message).toLowerCase() === 'token invalid'
}

export const isWidgetProjectPath = (pathname = '') =>
    /^\/dashboard\/projects\/[^/]+/.test(pathname)
